import {useState} from 'react';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {MAX_INIT_COLS, MAX_INIT_ROWS, isInsertableTableSize} from '../lib/table-ops';

// ─── TableSizePicker ─────────────────────────────────────────────────────────

/**
 * Size prompt shown in place of an empty table. Rows and columns are typed in
 * directly: the 8 × 8 hover grid this replaced (DERP-226 item 4) could not express
 * a larger table at all, and picking an exact size off a grid of squares was fiddly
 * even inside its range.
 */
export function TableSizePicker({onSelect}: {onSelect: (rows: number, cols: number) => void}) {
  // Held as text so a field can be cleared and re-keyed; the numbers are read only
  // on Insert, so nothing rewrites a half-typed value.
  const [rows, setRows] = useState('3');
  const [cols, setCols] = useState('3');

  const typedRows = Number(rows);
  const typedCols = Number(cols);
  const canInsert = isInsertableTableSize(typedRows, typedCols);
  const insert = () => {
    if (canInsert) onSelect(typedRows, typedCols);
  };

  return (
    <div className="flex flex-col items-center gap-3 py-6">
      <p className="text-xs text-muted-foreground font-medium">Table size</p>
      <div className="flex items-end gap-2">
        <label className="flex flex-col gap-0.5">
          <span className="text-[11px] text-muted-foreground">Rows</span>
          <Input
            type="number"
            value={rows}
            onChange={(e) => setRows(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && insert()}
            className="h-7 w-16 text-xs"
            title={`Rows (1–${MAX_INIT_ROWS})`}
          />
        </label>
        <span className="text-[11px] text-muted-foreground pb-1.5">×</span>
        <label className="flex flex-col gap-0.5">
          <span className="text-[11px] text-muted-foreground">Columns</span>
          <Input
            type="number"
            value={cols}
            onChange={(e) => setCols(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && insert()}
            className="h-7 w-16 text-xs"
            title={`Columns (1–${MAX_INIT_COLS})`}
          />
        </label>
        <Button size="sm" className="h-7 text-xs" disabled={!canInsert} onClick={insert}>
          Insert
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground h-4">
        {canInsert ? '' : `1–${MAX_INIT_ROWS} rows, 1–${MAX_INIT_COLS} columns`}
      </p>
    </div>
  );
}

// ─── TableElement ─────────────────────────────────────────────────────────────
